import {Color} from '@benchling/nucleus-ui';
import {Group, Sx, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {HandleType, NodeProps} from 'reactflow';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {TASK_STATUS_ICONS} from 'processes/status/TaskStatusIcon';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ellipsisText, multiLineEllipsisText} from 'styles/Utilities';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {InvisibleNodeHandle} from 'ui-library/reactflow/handles/InvisibleNodeHandle';
import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';

import PlanPill, {PlanPillVariant} from '../PlanPill';
import {PLANNED, UnitOperationStatusType} from '../useMultiProcedureRunStudyProgress';

export enum NodeType {
  SOURCE_PMEI = 'SOURCE_PMEI',
  DESTINATION_PMEI = 'DESTINATION_PMEI',
}

const ICON_SIZE = {
  height: 16,
  width: 16,
};

interface UnitOperationFlowchartNodeData {
  pmeiId: string;
  procedureRunName: string | null;
  isPlan?: boolean;
  methodName: string;
  conditionName: string;
  replicateNumber: number;
  status: UnitOperationStatusType | null;
}

interface NodeHeaderProps {
  nodeType: string;
  nodeId: string;
  title: string;
  subTitle: string;
  showPlanLabel: boolean;
  rightIcon?: BenchlingIconName;
  iconColor?: string;
  headerColor: string;
}

const UNIT_OPERATION_NODE_HEADER_COLORS: Record<UnitOperationStatusType, string> = {
  [ProcessTaskStatusType.COMPLETED]: Color.GREEN_100,
  [ProcessTaskStatusType.FAILED]: Color.GRAY_100,
  [ProcessTaskStatusType.IN_PROGRESS]: Color.BLUE_100,
  [ProcessTaskStatusType.SKIPPED]: Color.GRAY_100,
  [ProcessTaskStatusType.CANCELLED]: Color.GRAY_100,
  [ProcessTaskStatusType.PENDING]: Color.WHITE,
  [ProcessTaskStatusType.INVALID]: Color.GRAY_100,
  [PLANNED]: Color.WHITE,
};

function getIconStyleFromStatus(status: UnitOperationStatusType | null): {
  icon: BenchlingIconName | undefined;
  iconColor: string | undefined;
} {
  if (status == null) {
    return {icon: undefined, iconColor: undefined};
  }

  if (status === PLANNED) {
    return {
      icon: 'calendar-days',
      iconColor: Color.GRAY_600,
    };
  }

  const taskStatusInfo = TASK_STATUS_ICONS[status];
  const icon = taskStatusInfo?.icon;
  const iconColor = taskStatusInfo?.style.color;
  return {icon, iconColor};
}

function getNodeHeaderProps({
  nodeId,
  nodeType,
  methodName,
  procedureRunName,
  isPlan,
  status,
}: {
  nodeId: string;
  nodeType: string;
  methodName: string;
  procedureRunName: string | null;
  isPlan?: boolean;
  status: UnitOperationStatusType | null;
}): NodeHeaderProps {
  const {icon: rightIcon, iconColor} = getIconStyleFromStatus(status);
  const headerColor = status ? UNIT_OPERATION_NODE_HEADER_COLORS[status] : Color.WHITE;
  return {
    nodeId,
    nodeType,
    title: methodName,
    subTitle: procedureRunName ?? '',
    showPlanLabel: isPlan ?? false,
    rightIcon,
    iconColor,
    headerColor,
  };
}

/**
 * Generates handle IDs for unit operation flowchart nodes.
 * All nodes have source handles, but only destination nodes have target handles.
 */
function createHandleIdGenerator(nodeType: string) {
  return (nodeId: string, handleType: HandleType) =>
    nodeType === NodeType.DESTINATION_PMEI || handleType === 'source' ? `${nodeId}-${handleType}` : null;
}

function UnitOperationFlowchartNodeHeader({
  nodeId,
  nodeType,
  title,
  subTitle,
  showPlanLabel,
  rightIcon,
  iconColor,
  headerColor,
}: NodeHeaderProps): JSX.Element {
  const handleIdGenerator = createHandleIdGenerator(nodeType);

  const leftHandle =
    nodeType === NodeType.DESTINATION_PMEI ? (
      <InvisibleNodeHandle
        nodeId={nodeId}
        handleType="target"
        nodeHeaderHandleIdGenerator={handleIdGenerator}
      />
    ) : undefined;

  const rightHandle = (
    <InvisibleNodeHandle
      nodeId={nodeId}
      handleType="source"
      nodeHeaderHandleIdGenerator={handleIdGenerator}
    />
  );

  return (
    <BenchlingNodeV2.Header bg={headerColor}>
      {leftHandle}
      <Group spacing="sm" align="flex-start">
        <Text
          fw={FONT_WEIGHT_PROXIMA_SEMIBOLD}
          fz="md"
          sx={{
            flex: 1,
            ...(multiLineEllipsisText(2) as Sx),
          }}
        >
          {title}
        </Text>
        {rightIcon && (
          <BenchlingIcon
            icon={rightIcon}
            style={{
              ...ICON_SIZE,
              color: iconColor,
            }}
          />
        )}
      </Group>
      <div className={css(styles.subTitleContainer)} data-test="unit-operation-flowchart-node-subtitle">
        <span className={css(styles.subTitleText)}>{subTitle}</span>
        {showPlanLabel && <PlanPill variant={PlanPillVariant.FLOWCHART} />}
      </div>
      {rightHandle}
    </BenchlingNodeV2.Header>
  );
}

export default function UnitOperationFlowchartNode({
  data,
  ...props
}: NodeProps<UnitOperationFlowchartNodeData>): JSX.Element {
  const {methodName, procedureRunName, conditionName, replicateNumber, isPlan, status} = data;
  const nodeHeaderProps = getNodeHeaderProps({
    nodeId: props.id,
    nodeType: props.type,
    methodName,
    procedureRunName,
    isPlan,
    status,
  });
  const sections = [{leftHeader: `${conditionName} - ${replicateNumber}`, sectionRows: []}];

  return (
    <div className={css(styles.node)}>
      <BenchlingNodeV2 isSelectable={true} isSelected={props.selected}>
        <UnitOperationFlowchartNodeHeader {...nodeHeaderProps} />
        <BenchlingNodeV2.Standard.Sections sections={sections} />
      </BenchlingNodeV2>
    </div>
  );
}

const styles = StyleSheet.create({
  node: {
    ':hover': {
      cursor: 'default',
    },
  },
  subTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 8,
    minWidth: 0,
    maxWidth: '100%',
  },
  subTitleText: {
    display: 'inline-block',
    color: Color.GRAY_600,
    fontSize: 12,
    maxWidth: '100%',
    minWidth: 0,
    flex: `1 1 auto`,
    ...ellipsisText,
  },
});
